import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import {
  Box,
  Typography,
  Grid,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { 
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

interface TenantFeature {
  id: string;
  tenant_id: string;
  // 基本機能
  customers_enabled: boolean;
  products_enabled: boolean;
  documents_enabled: boolean;
  // コミュニケーション機能
  chat_enabled: boolean;
  staff_chat_enabled: boolean;
  // AI機能
  deep_research_enabled: boolean;
  legal_check_enabled: boolean;
  marketing_ai_enabled: boolean;
  workflow_ai_enabled: boolean;
  knowledge_base_enabled: boolean;
  // 管理機能
  staff_management_enabled: boolean;
  notifications_enabled: boolean;
  invitations_enabled: boolean;
  session_management_enabled: boolean;
  // メタデータ
  notes?: string;
  updated_at?: string;
  updated_by?: string;
}

const featureCategories = [
  {
    title: '基本機能',
    features: [
      { key: 'customers_enabled', label: '顧客管理', description: '顧客情報の管理・編集' },
      { key: 'products_enabled', label: '商品管理', description: '商品・サービス情報の管理' },
      { key: 'documents_enabled', label: 'AIドキュメント管理', description: 'ファイルアップロード・管理' },
    ]
  },
  {
    title: 'コミュニケーション',
    features: [
      { key: 'chat_enabled', label: 'チャット', description: '顧客とのチャット機能' },
      { key: 'staff_chat_enabled', label: 'スタッフチャット', description: '内部コミュニケーション' },
    ]
  },
  {
    title: 'AI機能',
    features: [
      { key: 'deep_research_enabled', label: 'Deep Research', description: '顧客・競合分析' },
      { key: 'legal_check_enabled', label: 'AI法務チェック', description: '契約書の自動チェック' },
      { key: 'marketing_ai_enabled', label: 'マーケティングAI', description: 'マーケティング支援' },
      { key: 'workflow_ai_enabled', label: 'ワークフローAI', description: '業務フロー最適化' },
      { key: 'knowledge_base_enabled', label: 'ナレッジベース', description: 'AI知識検索・法務文書管理' },
    ]
  },
  {
    title: '管理機能',
    features: [
      { key: 'staff_management_enabled', label: 'スタッフ管理', description: 'スタッフアカウント管理' },
      { key: 'notifications_enabled', label: '通知管理', description: 'システム通知の管理' },
      { key: 'invitations_enabled', label: '招待管理', description: 'ユーザー招待機能' },
      { key: 'session_management_enabled', label: 'セッション管理', description: 'ログインセッション管理' },
    ]
  }
];

interface TenantFeaturesTabProps {
  tenantId?: string;
  tenantName?: string;
}

function TenantFeaturesTab({ tenantId, tenantName = '' }: TenantFeaturesTabProps) {
  const [features, setFeatures] = useState<TenantFeature | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    if (tenantId) {
      loadTenantFeatures();
    }
  }, [tenantId]);

  const loadTenantFeatures = async () => {
    if (!tenantId) return;
    
    try {
      setLoading(true);
      const data = await apiService.getTenantFeatures(tenantId);
      setFeatures(data);
    } catch (error) {
      console.error('Failed to load tenant features:', error);
      setMessage({ type: 'error', text: `機能設定の読み込みに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}` });
    } finally {
      setLoading(false);
    }
  };


  const handleFeatureChange = (featureKey: string, enabled: boolean) => {
    if (!features) return;
    
    setFeatures({
      ...features,
      [featureKey]: enabled
    });
  };

  const handleSave = async () => {
    if (!features || !tenantId) return;
    
    try {
      setSaving(true);
      const updatedFeatures = await apiService.updateTenantFeatures(tenantId, features);
      setFeatures(updatedFeatures);
      setMessage({ type: 'success', text: '設定を保存しました' });
    } catch (error) {
      console.error('Failed to save features:', error);
      setMessage({ type: 'error', text: `設定の保存に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}` });
    } finally {
      setSaving(false);
    }
  };


  const getEnabledFeaturesCount = () => {
    if (!features) return 0;
    return featureCategories.reduce((count, category) => {
      return count + category.features.filter(feature => 
        (features as any)[feature.key]
      ).length;
    }, 0);
  };

  const getTotalFeaturesCount = () => {
    return featureCategories.reduce((count, category) => count + category.features.length, 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!features) {
    return (
      <Box>
        <Alert severity="error">機能設定を読み込めませんでした</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          機能設定
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadTenantFeatures}
          >
            更新
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? '保存中...' : '保存'}
          </Button>
        </Box>
      </Box>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* サマリー */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="textSecondary">
              有効な機能数
            </Typography>
            <Typography variant="h5" color="primary">
              {getEnabledFeaturesCount()} / {getTotalFeaturesCount()}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="textSecondary">
              最終更新
            </Typography>
            <Typography variant="body1">
              {features.updated_at ? new Date(features.updated_at).toLocaleString('ja-JP') : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="textSecondary">
              メモ
            </Typography>
            <Typography variant="body1">
              {features.notes || 'なし'}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* 機能設定 */}
      <Grid container spacing={2}>
        {featureCategories.map((category, categoryIndex) => (
          <Grid item xs={12} md={6} key={categoryIndex}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {category.title}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {category.features.map((feature) => (
                <FormControlLabel
                  key={feature.key}
                  sx={{ display: 'flex', ml: 0, mb: 1 }}
                  control={
                    <Switch
                      checked={(features as any)[feature.key]}
                      onChange={(e) => handleFeatureChange(feature.key, e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {feature.label}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </Box>
          </Grid>
        ))}
      </Grid>

    </Box>
  );
}

export default TenantFeaturesTab;